import { Link } from 'react-router-dom'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Heart, NotebookPen, Users, ArrowUpRight, Mail, ArrowRight } from 'lucide-react'

const CONTACT_EMAIL = 'support@davenportministries.com'

// Other apps built by the same small team.
const OTHER_APPS = [
  {
    icon: Users,
    name: 'Fellowship',
    tagline: 'This app',
    body: 'Discover and host Christian gatherings near you — potlucks, Bible studies, hikes, and more, across every denomination.',
    href: '/',
    internal: true,
  },
  {
    icon: Heart,
    name: 'SDA Match',
    tagline: 'For Adventist singles',
    body: 'A dating app that connects single Seventh-day Adventists with partners who share their faith and values.',
    href: 'https://sdamatch.app',
    internal: false,
  },
  {
    icon: NotebookPen,
    name: 'Tend Journal',
    tagline: 'Private AI journal',
    body: 'A privacy-first journaling app that uses on-device AI to help you reflect, notice patterns, and understand yourself a little better.',
    href: 'https://apps.apple.com/us/app/tend-journal/id6766641870',
    internal: false,
  },
]

export default function AboutPage() {
  return (
    <PublicLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="max-w-[1080px] mx-auto px-6 pt-16 pb-12">
        <div className="max-w-[680px]">
          <div className="inline-block text-[11px] font-semibold uppercase tracking-[0.1em] text-primary bg-primary-soft border border-primary rounded-full px-3 py-1 mb-5">
            About us
          </div>
          <h1 className="serif text-[52px] leading-[1.05] tracking-[-0.025em] text-ink m-0 mb-5">
            Bringing people together<br />
            <span className="serif-italic text-primary">in meaningful ways</span>
          </h1>
          <p className="text-[17px] text-ink-2 leading-[1.65] m-0 max-w-[560px]">
            Davenport Ministries is a small team building simple, thoughtful apps that help
            people connect — with their faith, with their communities, and with each other.
            Fellowship is one of those apps.
          </p>
        </div>
      </div>

      {/* ── Mission ───────────────────────────────────────────────────────── */}
      <div className="bg-s2 border-t border-b border-line-soft py-14">
        <div className="max-w-[720px] mx-auto px-6">
          <div className="eyebrow text-center mb-8">Why we build</div>
          <div className="flex flex-col gap-5 text-[16px] text-ink-2 leading-[1.7]">
            <p className="m-0">
              We believe technology should pull people out of their screens and into real rooms,
              around real tables, with real people. Too many tools are built to keep you scrolling.
              We'd rather help you show up somewhere.
            </p>
            <p className="m-0">
              Every project we take on starts with the same question: does this help someone feel
              a little less alone? Fellowship helps Christians find gatherings near them, no matter
              their denomination. Our other apps carry that same idea into different corners of life.
            </p>
          </div>
        </div>
      </div>

      {/* ── Other apps ────────────────────────────────────────────────────── */}
      <div className="max-w-[1080px] mx-auto px-6 py-14">
        <div className="eyebrow text-center mb-3">What else we build</div>
        <p className="text-center text-[14px] text-muted m-0 mb-10 max-w-[520px] mx-auto">
          A few of the other apps from our team. Different problems, same goal — helping people
          connect in ways that matter.
        </p>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {OTHER_APPS.map(({ icon: Icon, name, tagline, body, href, internal }) => {
            const cardClass =
              'group bg-surface border border-line-soft rounded-2xl p-6 flex flex-col no-underline transition-colors hover:border-primary'
            const inner = (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center">
                    <Icon size={18} className="text-primary" />
                  </div>
                  <ArrowUpRight
                    size={16}
                    className="text-muted transition-colors group-hover:text-primary"
                  />
                </div>
                <div className="text-[16px] font-semibold text-ink mb-1 leading-[1.3]">{name}</div>
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-primary mb-3">
                  {tagline}
                </div>
                <p className="text-[13px] text-ink-2 leading-[1.6] m-0">{body}</p>
              </>
            )
            return internal ? (
              <Link key={name} to={href} className={cardClass}>
                {inner}
              </Link>
            ) : (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={cardClass}
              >
                {inner}
              </a>
            )
          })}
        </div>
      </div>

      {/* ── Contact ───────────────────────────────────────────────────────── */}
      <div className="max-w-[680px] mx-auto px-6 pb-16">
        <div className="rounded-2xl bg-primary-soft border border-primary p-8 text-center">
          <div className="w-11 h-11 rounded-xl bg-surface flex items-center justify-center mx-auto mb-4">
            <Mail size={20} className="text-primary" />
          </div>
          <h2 className="serif text-[28px] text-ink m-0 mb-2">Let's talk</h2>
          <p className="text-[15px] text-ink-2 leading-[1.65] m-0 mb-6 max-w-[460px] mx-auto">
            Have an idea to make Fellowship better? Want to collaborate, partner, or just say hello?
            We read every message and would genuinely love to hear from you.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-primary text-on-primary text-[14px] font-semibold no-underline"
          >
            Get in touch <ArrowRight size={15} />
          </a>
          <p className="text-[12.5px] text-muted mt-4 m-0">
            Or email us directly at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary no-underline">
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>
      </div>
    </PublicLayout>
  )
}
